import { consumer, core, safeToNumber } from '@tock/coretypes';
import { displayInDollars } from '@tock/money';
import { find, replace, toString } from 'es-toolkit/compat';
import { oc } from 'ts-optchain';

export interface CustomDimensions {
  BOOKED_PERCENT: string;
  NUM_EXPERIENCES: string;
  HAS_EXTERNAL_LINK: string;
  TOCK_APP: string;
  SELECTED_METRO_AREA: string;
}

const formatWithCategory = (purchaseCategory: string, subcategory: string): string => {
  return `${purchaseCategory}/${subcategory}`;
};

const itemSchema = (
  id: string,
  name: string | undefined | null,
  category: string,
  brand: string | undefined,
  price: string,
  quantity: number
) => ({
  id,
  name,
  category,
  brand,
  price,
  quantity,
});

export const getExperienceObj = (
  purchaseOrder: core.PurchaseOrder,
  purchaseCategory: string,
  business: consumer.BusinessConfig
) =>
  itemSchema(
    `Ex_${safeToNumber(purchaseOrder.ticketTypeId)}`,
    purchaseOrder.ticketTypeName,
    formatWithCategory(purchaseCategory, 'Reservation'),
    oc(business).name(),
    displayInDollars(purchaseOrder.ticketTypePrice),
    purchaseOrder.ticketCount
  );

export const getOptionObj = (
  option: core.Price,
  purchaseCategory: string,
  business: consumer.BusinessConfig,
  optionMap: Dictionary<number>
) => {
  const quantity = optionMap[toString(safeToNumber(option.id))] || 0;
  if (quantity <= 0) {
    return;
  }

  return itemSchema(
    `Sp_${safeToNumber(option.id)}`,
    option.name,
    formatWithCategory(purchaseCategory, 'Supplement'),
    oc(business).name(),
    displayInDollars(option.amount),
    quantity
  );
};

export const getFeeObj = (
  fee: core.IFeePrice,
  purchaseCategory: string,
  business: consumer.BusinessConfig
) =>
  itemSchema(
    `Fe_${safeToNumber(fee.price?.id)}`,
    fee.price?.name,
    formatWithCategory(purchaseCategory, 'Fee'),
    oc(business).name(),
    displayInDollars(fee.price?.amount ?? 0),
    1
  );

export const getCustomChargeObj = (
  customCharge: core.CustomChargeOrder,
  purchaseCategory: string,
  business: consumer.BusinessConfig
) =>
  itemSchema(
    `Ch_${replace(customCharge.customChargeName, ' ', '_')}`,
    customCharge.customChargeName,
    formatWithCategory(purchaseCategory, 'Custom Charge'),
    oc(business).name(),
    displayInDollars(customCharge.priceCents),
    1
  );

export const getPromoCode = (purchaseOrder: core.PurchaseOrder) =>
  oc(find(purchaseOrder.discount, (discount: core.IDiscount) => !!discount.promoCode)).promoCode();
